import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  getCurrentUser, 
  signIn, 
  signOut, 
  signUp, 
  confirmSignUp, 
  resendSignUpCode,
  resetPassword, 
  confirmResetPassword,
  fetchUserAttributes,
  fetchAuthSession
} from 'aws-amplify/auth';
import { AuthenticatedUser, CognitoUserAttributes } from '../../../shared/types';
import { setTokenProvider } from '../utils/api';

// Configure Amplify (will be set from environment variables)
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID || '',
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    }
  }
};

Amplify.configure(amplifyConfig);

interface AuthContextType {
  user: AuthenticatedUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  const parseUserAttributes = (attributes: CognitoUserAttributes): AuthenticatedUser => {
    const isSystemAdmin = attributes['custom:system_admin'] === 'true';
    const isStorageAdmin = attributes['custom:storage_admin'] === 'true';
    
    const roles: ('user' | 'system_admin' | 'storage_admin')[] = ['user'];
    if (isSystemAdmin) roles.push('system_admin');
    if (isStorageAdmin) roles.push('storage_admin');

    return {
      userId: attributes.sub,
      email: attributes.email,
      roles,
      isSystemAdmin,
      isStorageAdmin,
    };
  };

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      
      // Fetch user attributes to get custom role attributes
      const attributes = await fetchUserAttributes();
      
      const cognitoAttributes: CognitoUserAttributes = {
        sub: currentUser.userId,
        email: attributes.email || '',
        email_verified: attributes.email_verified === 'true',
        'custom:system_admin': attributes['custom:system_admin'] || 'false',
        'custom:storage_admin': attributes['custom:storage_admin'] || 'false'
      };
      
      setUser(parseUserAttributes(cognitoAttributes));
    } catch (error) {
      console.log('No authenticated user found:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Token refresh logic
  const refreshTokens = async (): Promise<boolean> => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      return !!session.tokens?.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
      return false;
    }
  };

  // Set up automatic token refresh
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    const setupTokenRefresh = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          // Refresh tokens 5 minutes before expiration
          const expirationTime = (session.tokens.accessToken.payload.exp || 0) * 1000;
          const refreshTime = expirationTime - Date.now() - (5 * 60 * 1000); // 5 minutes before expiry
          
          if (refreshTime > 0) {
            refreshInterval = setTimeout(async () => {
              const refreshed = await refreshTokens();
              if (refreshed) {
                setupTokenRefresh(); // Set up next refresh
              }
            }, refreshTime);
          }
        }
      } catch (error) {
        console.log('No active session for token refresh setup');
      }
    };

    if (user) {
      setupTokenRefresh();
    }

    return () => {
      if (refreshInterval) {
        clearTimeout(refreshInterval);
      }
    };
  }, [user]);

  useEffect(() => {
    checkAuthState();
    
    // Set up token provider for API calls
    setTokenProvider(getAccessToken);
  }, []);

  const handleSignIn = async (email: string, password: string) => {
    try {
      await signIn({ username: email, password });
      await checkAuthState();
    } catch (error) {
      throw error;
    }
  };

  const handleSignUp = async (email: string, password: string) => {
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          }
        }
      });
    } catch (error) {
      throw error;
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
    } catch (error) {
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      await resetPassword({ username: email });
    } catch (error) {
      throw error;
    }
  };

  const handleConfirmResetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    } catch (error) {
      throw error;
    }
  };

  const refreshUser = async () => {
    await checkAuthState();
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn: handleSignIn,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signOut: handleSignOut,
    resetPassword: handleResetPassword,
    confirmResetPassword: handleConfirmResetPassword,
    refreshUser,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};